import {
  USER_MODEL_CLASS_NAME,
  USER_MODEL_KEY_POINTER
} from 'models/daos/user/constants'
import { loadCartAction } from 'modules/cart/actions'
import { resetCredentialAction } from 'modules/credential/actions'
import { CREDENTIAL_DOMAIN } from 'modules/credential/constants'
import { initCurrenciesAction } from 'modules/currencies/actions'
import { CURRENCIES_DOMAIN } from 'modules/currencies/constants'
import { loadNotificationsAction } from 'modules/notifications/actions/load'
import { createRemoteInstanceLegacyRedux } from 'modules/redux/actions/legacy/create'
import {
  loadingLegacyReduxAction,
  loadLegacyReduxAction,
  loadSuccessLegacyReduxAction
} from 'modules/redux/actions/legacy/load'
import { updateLegacyReduxAction } from 'modules/redux/actions/legacy/update'
import { validateLegacyReduxAction } from 'modules/redux/actions/legacy/validate'
import resetReduxAction from 'modules/redux/actions/reset'
import setReduxAction from 'modules/redux/actions/set'
import REDUX_ACTION from 'modules/redux/constants/action'
import REDUX_OPTION from 'modules/redux/constants/option'
import { getNewInstanceDomainSelector } from 'modules/redux/selectors/domain'
import getDomainListPathReduxUtil from 'modules/redux/utils/get-domain-list-path'
import getPathReduxUtil from 'modules/redux/utils/get-path'
import { delReduxUtil } from 'modules/redux/utils/legacy/del'
import { getValidationReduxUtil } from 'modules/redux/utils/legacy/get-validation'
import { ROUTE_PATH } from 'modules/routes/constants/index.cjs'
import { loadSettingsAction } from 'modules/settings/actions'
import {
  USERS_DOMAIN,
  USERS_ENDPOINT,
  USERS_PATH
} from 'modules/users/constants'
import getCurrentUserSelector from 'modules/users/selectors/get-current'
import pushRouterAction from 'modules/router/actions/push'
import { handleNewCustomerDiscountExpiresAtUserAction } from 'modules/training/modules/training-users/actions/handleNewCustomerDiscountExpiresAt/index.js'

function authenticateUserAction (options) {
  return dispatch => {
    return dispatch(loadLegacyReduxAction({
      endpoint: USERS_ENDPOINT.AUTHENTICATE,
      path: USERS_DOMAIN,
      className: USER_MODEL_CLASS_NAME,
      options
    }))
      .then(user => {
        dispatch(setCurrentUser(user.name))
        return user
      })
      .catch(() => {
      })
  }
}

/**
 * Sends the credential to the given endpoint.
 * @private
 * @param {string} endpoint
 * @param {Object} [options={}]
 * @returns {Function}
 */
function sendCredential (endpoint, options = {}) {
  return async (dispatch, getState) => {
    const credential = getState().credential
    if (!options.shouldNotValidate) {
      dispatch(validateLegacyReduxAction(CREDENTIAL_DOMAIN,
        USER_MODEL_CLASS_NAME
      ))
      if (getValidationReduxUtil(credential) !== true) {
        return
      }
    }

    const credentialData = credential.data
    const changed = credential.changed
    const {
      email,
      password
    } = {
      ...credentialData, ...changed
    }

    try {
      const user = await dispatch(createRemoteInstanceLegacyRedux({
        endpoint,
        domainListPath: getDomainListPathReduxUtil(USERS_DOMAIN),
        className: USER_MODEL_CLASS_NAME,
        data: {
          email,
          password,
          className: USER_MODEL_CLASS_NAME
        }
      }))
      dispatch(onAfterSignIn(user))
    } catch (error) {
      const user = getNewInstanceDomainSelector(USERS_DOMAIN)(getState())
      dispatch(setReduxAction({
        payload: {
          value: delReduxUtil(getState(), getPathReduxUtil(user))
        }
      }))
    }
  }
}

function signUpUserAction () {
  return sendCredential(USERS_ENDPOINT.SIGNUP)
}

function signInUserAction () {
  return sendCredential(USERS_ENDPOINT.SIGNIN)
}

function signUpGuestUserAction () {
  return sendCredential(USERS_ENDPOINT.SIGN_UP_GUEST, {
    shouldNotValidate: true
  })
}

/**
 * Sign in the user.
 * @param  {string} social
 * @param  {Object} data
 * @returns {Promise}
 */
function signInSocialUserAction (social, data) {
  return dispatch => {
    if (!data || !Object.keys(data).length) {
      return
    }
    // Get the user
    const { user } = data
    // Create the user and mark it as a loading
    const domainMapValue = user[USER_MODEL_KEY_POINTER]
    const path = `${USERS_DOMAIN}.data.${domainMapValue}`
    dispatch(loadingLegacyReduxAction({
      path,
      domainMapValue
    }))
    // Mark the user as loaded
    dispatch(loadSuccessLegacyReduxAction({
      path,
      data: user
    }))
    dispatch(onAfterSignIn(user))
  }
}

function onAfterSignIn (user, options) {
  return dispatch => {
    dispatch(setCurrentUser(user.name))
    // Reset the credentials
    dispatch(resetCredentialAction())
    return dispatch(loadDataUserAction(options))
  }
}

/**
 * Loads the user's data, like settings and notifications.
 * @param  {Object} options
 * @returns {Function}
 */
function loadDataUserAction (options) {
  return dispatch => {
    return Promise.all([
      dispatch(loadCartAction(options)),
      dispatch(loadNotificationsAction(options)),
      dispatch(loadSettingsAction(options))
      // dispatch(loadRatesCurrenciesAction())
    ]).then(() => {
      dispatch(handleNewCustomerDiscountExpiresAtUserAction())
      return dispatch(initCurrenciesAction())
    })
  }
}

/**
 * Sets the currentUser name.
 * @private
 * @param  {string} name
 * @returns {Function}
 */
function setCurrentUser (name) {
  return updateLegacyReduxAction({
    path: USERS_PATH.CURRENT,
    value: name
  })
}

/**
 * Signout the current user.
 * @param  {Object} data
 * @returns {Promise}
 */
function signOutUserAction () {
  return (dispatch, getState) => {
    const currentUser = getCurrentUserSelector()(getState())
    dispatch(loadLegacyReduxAction({
      endpoint: USERS_ENDPOINT.SIGNOUT,
      path: getPathReduxUtil(currentUser),
      options: {
        [REDUX_OPTION.FORCED]: true
      }
    }))
      .then(() => {
        dispatch(pushRouterAction(ROUTE_PATH.INDEX))
        dispatch(resetReduxAction({
          type: REDUX_ACTION.RESET,
          keysToOmit: [
            CURRENCIES_DOMAIN
          ]
        }))
      })
  }
}

/**
 * Changes the current's user property.
 * @param  {string} path
 * @param  {string} key
 * @param  {*} value
 * @returns {Function}
 */
function updateUserAction (path, key, value) {
  return updateLegacyReduxAction({
    path,
    key,
    value
  })
}

export {
  authenticateUserAction,
  loadDataUserAction,
  signUpGuestUserAction,
  signUpUserAction,
  signInUserAction,
  signInSocialUserAction,
  signOutUserAction,
  updateUserAction
}
